/**
 * 收款入口（PaymentEntry）类型定义（payment-entries-links-and-qr:ADM-10）。
 *
 * 字段权威源：`prototype/shared/src/types/domain.ts:436-473`（PaymentEntryBase/PaymentQr/PaymentLink）。
 * 三端不共享包（角色语义隔离，对齐 miniapp-shared 头部约束），各端手工对齐此基础类型。
 *
 * 禁止字段（D-01）：usageMode（当面/远程）、customValidUntil（自定义有效期）、manualRevoked（人工失效）；
 * 终态统一 CLOSED，不单列 EXPIRED。
 */
import type { ReceivableType } from '../api/billing.api';

/** 入口形态：LINK 收款链接 / QR 收费二维码。 */
export type PaymentEntryType = 'LINK' | 'QR';

/** 入口存储态状态机（D5：WAITING_SCAN → WAITING_PAYMENT → PAID/CLOSED，终态统一 CLOSED 不单列 EXPIRED）。 */
export type PaymentEntryStatus = 'WAITING_SCAN' | 'WAITING_PAYMENT' | 'PAID' | 'CLOSED';

/** 收款入口基础字段（对齐 prototype domain.ts:436-458）。 */
export interface PaymentEntryBase {
  id: string;
  orderId: string;
  /**
   * 关联的应收节点：尾款场景生成时即绑定；首笔款项场景在家长选定首付款/全款并支付前为空，
   * 支付成功后回填。
   */
  receivablePlanId?: string;
  studentId: string;
  entryType: PaymentEntryType;
  /** 系统算出的应收金额（分）；款项形态未定时不落值，由订单商品快照派生展示金额。 */
  amount?: number;
  /** 员工是否授权家长只付首付款（否则只能一次性结清全款）；尾款入口恒为 false。 */
  allowDeposit: boolean;
  /** 归属监护人手机号的 sha256 哈希；明文手机号不落入 payment_entries。 */
  guardianMobileHash: string;
  /** 192-bit 不可猜测令牌（base64url ≥32 字符，对齐 design D4）。 */
  token: string;
  createdByStaffId: string;
  campusId: string;
  status: PaymentEntryStatus;
  createdAt: string;
  openCount: number;
  lastOpenedAt?: string;
}

/** 收费二维码。 */
export interface PaymentQr extends PaymentEntryBase {
  entryType: 'QR';
}

/** 收款链接。 */
export interface PaymentLink extends PaymentEntryBase {
  entryType: 'LINK';
}

export type PaymentEntry = PaymentQr | PaymentLink;

/** 生成收款入口请求载荷（SC-01：MUST NOT 携带金额字段，金额由系统按订单商品快照派生）。 */
export interface CreatePaymentEntryPayload {
  orderId: string;
  entryType: PaymentEntryType;
  /** 尾款场景生成时即绑定应收节点；首笔款项场景为空。 */
  receivablePlanId?: string;
  /** 默认 false（家长仅可一次性结清全款）。 */
  allowDeposit?: boolean;
}

/** 修改分期授权请求载荷（SC-08：仅首笔款项入口生成后支付前可改）。 */
export interface UpdateAllowDepositPayload {
  allowDeposit: boolean;
}

/** 款项候选（对齐 prototype derive.ts:96-102 PaymentOption）。 */
export interface PaymentOption {
  /** 款项类型，与应收节点类型一致。 */
  type: ReceivableType;
  amount: number;
  /** 已绑定的应收节点（尾款或形态已锁定场景）；未定形态时为空，由支付动作生成。 */
  receivablePlanId?: string;
}

/**
 * 订单款项上下文（收款入口对话框派生可付金额用，挂载点从订单详情传入）。
 * 裁决规则对齐原型 derive.ts paymentEntryPayableOptions：入口授权 + 订单款项形态的唯一裁决点。
 */
export interface PaymentEntryOrderContext {
  /** 应收总额（分）。 */
  receivableAmountMinor: number;
  /** 首付比例（0~1），派生首付款金额。 */
  depositRatio: number;
  /** 当前待收节点摘要；null 表示款项形态未定（首笔支付前）。 */
  currentReceivablePlan: {
    type: ReceivableType;
    amountMinor: number;
    paidAmountMinor: number;
  } | null;
}

/**
 * 收款入口视图（对话框/详情，含读时派生字段）。
 * SC-26/27/28：对话框展示类型/款项/授权/生成员工/时间/有效期/状态。
 */
export interface PaymentEntryView extends PaymentEntryBase {
  /** 读时派生状态（合并有效期 + 订单/节点状态，D5 唯一裁决点）。 */
  derivedStatus: PaymentEntryStatus;
  /** 派生有效期（createdAt + paymentLinkExpireMinutes，无 validUntil 列，对齐数据模型）。 */
  validUntil: string;
  /** 派生：生成员工姓名。 */
  createdByName: string;
  /** 派生：学生姓名（脱敏，对齐 SC-14 孩子脱敏姓名）。 */
  studentName: string;
  /** 派生：校区名称。 */
  campusName: string;
  /** 款项节点摘要（尾款场景或已绑定节点时存在）。 */
  receivablePlanSummary?: {
    receivablePlanId: string;
    type: ReceivableType;
    amount: number;
    paidAmount: number;
    dueAt: string | null;
    status: string;
  };
  /** 支付通道（IKBFM3：商户号未配置走 mock，到位即 real；缺省视为 mock）。 */
  paymentChannel?: 'mock' | 'real';
  /** 二维码编码内容（服务端下发，`${base}/pay/${token}`）。 */
  qrContent?: string;
  /** 二维码 PNG 相对路径（/api/payment-entries/{token}/qr，与 API 同源直连）。 */
  qrImageUrl?: string;
}

/** 模拟家长已支付结果（IKBFM3 mock 通道，幂等重放返回既有事实）。 */
export interface MockPayPaymentEntryResult {
  entryId: string;
  orderId: string;
  status: 'PAID' | 'FAILED';
  businessPaymentNo: string | null;
  paymentTransactionId: string | null;
  idempotent: boolean;
  failure: string | null;
}

/** 按订单查询入口列表参数。 */
export interface PaymentEntryListQuery {
  orderId: string;
}

/** 入口状态展示映射。 */
export const PAYMENT_ENTRY_STATUS_LABEL: Record<PaymentEntryStatus, string> = {
  WAITING_SCAN: '待扫码',
  WAITING_PAYMENT: '待支付',
  PAID: '已支付',
  CLOSED: '已关闭',
};

/** 入口形态展示映射。 */
export const PAYMENT_ENTRY_TYPE_LABEL: Record<PaymentEntryType, string> = {
  LINK: '收款链接',
  QR: '收费二维码',
};
